<template>
  <div class="scroll-wrapper" ref="wrapper" :class="{'show-content': contentOpen, 'mobile': mobile}" @scroll="scrollChange">
    <div class="scroll-container" ref="container">
      <section ref="start">
        <section-intro />
      </section>
      <section ref="profil">
        <section-profile />
      </section>
      <section ref="chronik" class="add-margin">
        <section-timeline />
      </section>
      <section ref="werte">
        <section-values @scroll-to="setScrollTarget" />
      </section>
      <section ref="finanzbericht">
        <section-report />
      </section>
      <div class="output">{{ isTouchPad }}</div>
    </div>
  </div>
</template>

<script>
  import { gsap, Cubic } from 'gsap';
  import { ScrollToPlugin } from 'gsap/ScrollToPlugin';
  import MobileDetect from "mobile-detect";
  import Hammer from "hammerjs";
  import normalizeWheel from 'normalize-wheel';
  import SectionIntro from "../views/SectionIntro";
  import SectionProfile from "../views/SectionProfile";
  import SectionTimeline from "../views/SectionTimeline";
  import SectionValues from "../views/SectionValues";
  import SectionReport from "../views/SectionReport";

  gsap.registerPlugin(ScrollToPlugin);

  export default {
    name: "HorizontalScroller",
    components: {SectionReport, SectionValues, SectionTimeline, SectionProfile, SectionIntro},
    data () {
      return {
        scrollPosition: 0,
        showContent: false,
        isTouchPad: false,
        isHorizontal: false,
        sectionOffsets: [0, 0, 0, 0, 0],
        currentSection: 0,
        mobile: false
      }
    },
    props: {
      preventScroll: {
        type: Boolean,
        default: false
      },
      contentOpen: {
        type: Boolean,
        default: false
      },
      scrollTarget: {
        type: String,
        default: '/'
      }
    },
    watch: {
      scrollTarget() {
        const t = this;
        const anchor = this.scrollTarget === '/' ? 'start' : this.scrollTarget.substring(1, this.scrollTarget.length - 1);
        if (window.innerWidth > 1000) {
          const target = this.$refs[ anchor ].offsetLeft;
          const time = Math.round( Math.abs(target - this.$refs.wrapper.scrollLeft) / 10 ) + 500;
          gsap.to(this.$refs.wrapper, {
            scrollTo: {x: target, y: 0},
            duration: time / 1000,
            ease: Cubic.easeInOut,
            onComplete() {
              t.scrollPosition = target;
            }
          })
        }
        else {
          const target = this.$refs[ anchor ].offsetTop;
          const time = Math.round( Math.abs(target - window.scrollY) / 10 ) + 500;
          gsap.to(window,{
            scrollTo: {x: 0, y: target},
            duration: time / 1000,
            ease: Cubic.easeInOut
          })
        }
      }
    },
    emits: ['scroll-change', 'change-content', 'prevent-scroll', 'is-touch-pad', 'scroll-to'],
    methods: {
      handleWheel: function (e) {
        this.checkTouchPad(e);

        const w = this.$refs.wrapper;
        const normalized = normalizeWheel(e);

        if (w && normalized && window.outerWidth > 1000 && !this.preventScroll) {
          this.checkTouchPad(e);
          this.scrollPosition += parseInt(normalized.pixelY);
          if (this.scrollPosition < 0) {
            this.scrollPosition = 0;
          }
          if (this.scrollPosition > w.scrollWidth - w.offsetWidth) {
            this.scrollPosition = w.scrollWidth - w.offsetWidth;
          }
          if (this.isTouchPad) {
            w.scrollLeft = this.scrollPosition;
          }
          else {
            gsap.to(w, {
              scrollTo: {x: this.scrollPosition, y: 0},
              duration: 0.3
            })
          }
        }
      },

      scrollChange() {
        const sl = this.$refs.wrapper.scrollLeft;
        const so = this.sectionOffsets;
        for (let i = 0; i < so.length; i++) {
          if (sl < so[i]) {
            this.currentSection = i - 1;
            this.$emit('scroll-change', {
              current: this.currentSection,
              progress: (sl - so[i-1]) / (so[i] - so[i-1])
            });
            return;
          }
        }
      },

      checkTouchPad(e) {
        if (!this.isTouchPad) {
          this.isTouchPad = e.wheelDeltaY ? e.wheelDeltaY === -3 * e.deltaY : e.deltaMode === 0;
          if (this.isTouchPad) {
            this.$emit('is-touch-pad');
          }
        }
      },

      resize() {
        const wasHorizontal = this.isHorizontal;
        this.isHorizontal = window.innerWidth > 999;

        if (wasHorizontal && !this.isHorizontal) {
          this.scrollPosition = 0;
          this.$refs.wrapper.scrollLeft = 0;
        }
        if (this.isHorizontal && !wasHorizontal) {
          window.scrollTo(0, 0);
        }

        if (this.isHorizontal) {
          this.getSectionArray();
        }
      },

      getSectionArray() {
        this.sectionOffsets[1] = this.$refs.profil.offsetLeft;
        this.sectionOffsets[2] = this.$refs.chronik.offsetLeft;
        this.sectionOffsets[3] = this.$refs.werte.offsetLeft;
        this.sectionOffsets[4] = this.$refs.finanzbericht.offsetLeft;
        this.sectionOffsets[5] = this.$refs.wrapper.scrollWidth;
      },

      setScrollTarget(data) {
        const t = this;
        gsap.to(window.innerWidth < 1000 ? window : this.$refs.wrapper,{
          scrollTo: {x: data.target.x, y: data.target.y},
          duration: data.delay / 1000,
          ease: Cubic.easeInOut,
          onComplete() {
            t.scrollPosition = data.target.x;
          }
        })
      },

      initHammer() {
        const w = this.$refs.wrapper;
        const hammer = new Hammer(w);
        let startValue = 0;

        hammer.on('panstart', () => {
          startValue = w.scrollLeft;
        });

        hammer.on('pan',(e) => {
          this.scrollPosition = startValue - e.deltaX;
          w.scrollLeft = this.scrollPosition;
        });

        hammer.on('panend',(e) => {
          this.scrollPosition -= Math.round(e.velocity * 150);
          //this.scrollPosition += Math.round(e.velocity * 50);
          //t.container.removeClass('dragging');
          gsap.to(w,{
            scrollTo: {x: this.scrollPosition, y: 0},
            duration: 0.4,
            ease: Cubic.easeOut
          })
        });
      }
    },

    mounted() {
      const detect = new MobileDetect(window.navigator.userAgent);
      this.mobile = detect.mobile();
      if (!this.mobile) {
        document.addEventListener('wheel', this.handleWheel);
        this.initHammer();
      }
      window.addEventListener('resize', this.resize);
      setInterval(() => {
        this.resize();
      },2500)
      this.resize();
    }
  }
</script>

<style scoped>
  .output {
    position: absolute;
    top: 0;
    left: 0;
    z-index: 3;
    visibility: hidden;
  }
  @media(min-width: 1000px) {
    .scroll-wrapper {
      position: absolute;
      display: block;
      top: 0;
      left: 0;
      width: 100%;
      height: 100vh;
      transition: transform 0.6s cubic-bezier(0.33, 1, 0.68, 1);
      overflow: hidden;
    }
    .scroll-wrapper.mobile {
      overflow-x: auto;
    }
    .scroll-wrapper.show-content {
      transform: translate3d(0, -60%, 0);
    }
    .scroll-container {
      display: flex;
      width: 100%;
      height: 100vh;
      flex-wrap: wrap;
      flex-direction: column;
    }
    section {
      min-width: 100vw;
      height: 100%;
      display: flex;
      align-items: center;
      justify-content: center;
    }
    .add-margin {
      margin-left: 11vh;
    }
  }
  @media(max-width: 999px) {
    .scroll-wrapper {
      max-width: 100%;
      overflow: hidden;
    }
    .scroll-container {
      overflow: hidden;
    }
  }
  @media(max-width: 567px) {
    .add-margin {
      margin-top: 15vw;
    }
  }
</style>